import { useContext } from 'react'
import { useSelector } from 'react-redux'
import { selectIsExternalUser, selectIsInternalUser, selectTeamList } from 'state/profile'
import { TeamState } from 'state/teams'
import {
    OtherInternalTeamManagement,
    OwnTeamManagement,
    OtherExternalTeamManagement,
    CampaignStatusControl,
    TemplatedCampaignManagement,
} from 'components/Shared/Permission/enums'
import Context from 'components/Shared/Permission/PermissionsContext'
import { TeamState as ProfileTeamState } from 'state/profile'
import { CampaignManagement } from 'components/Shared/Permission/enums'
import { CAMPAIGN_STATUS_ENUM } from 'state/campaigns'
import { isCampaignLiveStatus } from 'components/utils'

/**
 * Hook to check page permissions are in user permissions
 *
 * @param permissions - [ permissionCode , permissionCode ...]
 * @returns {[permission]: boolean - user has this permission or not , ... } | null - if there isn't any permissions been pass to the function
 *
 */

export const useCheckPermissions = (permissions: string[]) => {
    const { isAllowedTo } = useContext(Context)
    return isAllowedTo(permissions)
}

/**
 *
 * Hook to check if some of the permissions provided have the same value as the condition
 *
 * @param permissions
 * @param condition
 * @returns True or False if one or more permission provided have the same value as the condition
 */
export const useCheckSomePermissions = (permissions: string[], condition = true) => {
    const checkedPermissions = useCheckPermissions(permissions)

    return (
        checkedPermissions && Object.values(checkedPermissions).some(permissionResult => permissionResult == condition)
    )
}

/**
 * Hook to check permission
 *
 * @param permission - permissionCode
 * @returns True if user have the permission
 *
 */

export const usePermission = (permission: string) => {
    const permissions = useCheckPermissions([permission])
    return permissions ? permissions[permission] : false
}

const getTeamPermissionCode = (selectedTeam: TeamState, userTeamList: ProfileTeamState[], isAssignRoles = false) => {
    const selectTeamIsInternalTeam = selectedTeam?.is_internal
    const isOwnTeam = userTeamList.findIndex(team => team.id === selectedTeam?.uuid) !== -1
    let code = ''
    if (isOwnTeam) {
        code = isAssignRoles ? OwnTeamManagement.AssignRoles : OwnTeamManagement.ManageRoles
    } else if (selectTeamIsInternalTeam) {
        code = isAssignRoles ? OtherInternalTeamManagement.AssignRoles : OtherInternalTeamManagement.ManageRoles
    } else {
        code = isAssignRoles ? OtherExternalTeamManagement.AssignRoles : OtherExternalTeamManagement.ManageRoles
    }

    return code
}

/**
 * Hook to check team permission via returned function
 *
 * @returns Function that checks if the user have a permission to a team
 *
 */

export const useCheckTeamPermissionFunction = () => {
    const userTeamList = useSelector(selectTeamList)
    const { isAllowedTo } = useContext(Context)

    return (selectedTeam: TeamState, isAssignRoles = false) => {
        const code = getTeamPermissionCode(selectedTeam, userTeamList, isAssignRoles)
        const isPermittedObject = isAllowedTo([code])
        return isPermittedObject ? isPermittedObject[code] : false
    }
}

/**
 *
 * Hook to check if some of the permissions provided have the same value as the condition
 *
 * @param selectedTeam
 * @param isAssignRole  if has this params then will check the assign roles permission otherwise will check the manage roles
 * @returns True or False if the permissions and team condition are both pass
 */
export const useCheckTeamPermission = (selectedTeam: TeamState, isAssignRoles = false) => {
    const checkTeamPermission = useCheckTeamPermissionFunction()

    return checkTeamPermission(selectedTeam, isAssignRoles)
}

// Function to get the right permission to check based on campaign status
const getEditCampaignPermissionsCode = (
    campaignStatus?: CAMPAIGN_STATUS_ENUM,
    checkForLiveEditBrief = false,
    checkForLiveEditDeadline = false,
    createAndEditBriefsPermissions: string[] = [],
    waitForPaymentPermission?: string,
    liveEditBriefPermission?: string,
    liveEditDeadlinePermission?: string,
): string[] => {
    const isCampaignDraftOrForEdit = [CAMPAIGN_STATUS_ENUM.DRAFT, CAMPAIGN_STATUS_ENUM.FOR_EDIT, undefined].includes(
        campaignStatus,
    )
    const isCampaignForReview = campaignStatus === CAMPAIGN_STATUS_ENUM.FOR_REVIEW
    const isCampaignWaitForPayment = campaignStatus === CAMPAIGN_STATUS_ENUM.WAIT_FOR_PAYMENT
    const isCampaignReadyForLaunch = campaignStatus === CAMPAIGN_STATUS_ENUM.READY_FOR_LAUNCH
    const isCampaignLive = isCampaignLiveStatus(campaignStatus)

    if (isCampaignDraftOrForEdit) {
        return createAndEditBriefsPermissions
    } else if (isCampaignForReview || isCampaignReadyForLaunch) {
        return [CampaignStatusControl.LaunchCampaign]
    } else if (isCampaignWaitForPayment) {
        const permissions: string[] = [CampaignStatusControl.LaunchCampaign]

        if (waitForPaymentPermission?.length) {
            permissions.push(waitForPaymentPermission)
        } else {
            permissions.push(CampaignStatusControl.PayCampaign)
        }

        return permissions
    } else if (isCampaignLive) {
        const permissions: string[] = []
        if (checkForLiveEditBrief && liveEditBriefPermission) {
            permissions.push(liveEditBriefPermission)
        }
        if (checkForLiveEditDeadline && liveEditDeadlinePermission) {
            permissions.push(liveEditDeadlinePermission)
        }
        return permissions
    }
    return []
}

/**
 *
 * Hook to check if campaign editor components view display condition
 * @param campaignStatus
 * @param isEditBrief default false for save and submit restricted container usage in saveForm container which will check either live campaign brief or live deadline
 * @param isEditDeadline default false , if need to check deadline permission will be true
 * @param isAbleToLaunch default false , determine which permission to control SaveAndSubmitButtonGroup display in wait for payment status
 * @returns True or False for each campaignStatus and its permission check result
 */

export const useCheckEditCampaignPermission = (
    campaignStatus?: CAMPAIGN_STATUS_ENUM,
    isEditBrief = false,
    isEditDeadline = false,
    isAbleToLaunch = false,
) => {
    const waitForPaymentPermission = isAbleToLaunch
        ? CampaignStatusControl.LaunchCampaign
        : CampaignStatusControl.PayCampaign
    const code = getEditCampaignPermissionsCode(
        campaignStatus,
        // FIXME: Need to fix the old campaign editor behavior that by default add Live campaign edit brief permission
        // if both isEditBrief and isEditDeadline is false
        !isEditBrief && !isEditDeadline ? true : isEditBrief,
        isEditDeadline,
        [CampaignManagement.EditBriefsForExternal, CampaignManagement.CreateAndEditBriefsForInternal],
        waitForPaymentPermission,
        CampaignManagement.LiveCampaignEditBriefs,
        CampaignManagement.LiveCampaignEditDeadlines,
    )

    const checkPermissions = useCheckSomePermissions(code)

    return campaignStatus === undefined ||
        [
            CAMPAIGN_STATUS_ENUM.DRAFT,
            CAMPAIGN_STATUS_ENUM.FOR_EDIT,
            CAMPAIGN_STATUS_ENUM.LIVE,
            CAMPAIGN_STATUS_ENUM.EXPIRED,
            CAMPAIGN_STATUS_ENUM.FULFILLED,
            CAMPAIGN_STATUS_ENUM.PAID_OUT,
            CAMPAIGN_STATUS_ENUM.FOR_REVIEW,
            CAMPAIGN_STATUS_ENUM.READY_FOR_LAUNCH,
        ].includes(campaignStatus)
        ? checkPermissions
        : false
}

/**
 *
 * Hook to check if campaign template components view display condition
 * @param campaignStatus
 * @param checkForLiveEditBriefAndDeadline Set to true if need to allow live campaign edit of brief and deadline
 * @param launchFieldEditable Set to true if need to allow edit when able to Launch
 * @returns True or False for each campaignStatus and its permission check result
 */

export const useCheckEditTemplatedCampaignPermission = (
    campaignStatus?: CAMPAIGN_STATUS_ENUM,
    checkForLiveEditBriefAndDeadline = false,
    isBudgetField = false,
) => {
    const code = getEditCampaignPermissionsCode(
        campaignStatus,
        checkForLiveEditBriefAndDeadline,
        checkForLiveEditBriefAndDeadline,
        [
            TemplatedCampaignManagement.CreateAndEditBriefsForExternal,
            TemplatedCampaignManagement.CreateAndEditBriefsForInternal,
        ],
        undefined,
        TemplatedCampaignManagement.LiveCampaignEditBriefsAndDeadlines,
        undefined,
    )

    const checkPermissions = useCheckSomePermissions(code)

    if (
        campaignStatus &&
        isBudgetField &&
        [
            CAMPAIGN_STATUS_ENUM.WAIT_FOR_PAYMENT,
            CAMPAIGN_STATUS_ENUM.READY_FOR_LAUNCH,
            CAMPAIGN_STATUS_ENUM.LIVE,
            CAMPAIGN_STATUS_ENUM.EXPIRED,
            CAMPAIGN_STATUS_ENUM.FULFILLED,
            CAMPAIGN_STATUS_ENUM.PAID_OUT,
        ].includes(campaignStatus)
    ) {
        return false
    }

    return campaignStatus === undefined ||
        [
            CAMPAIGN_STATUS_ENUM.DRAFT,
            CAMPAIGN_STATUS_ENUM.FOR_EDIT,
            CAMPAIGN_STATUS_ENUM.LIVE,
            CAMPAIGN_STATUS_ENUM.FOR_REVIEW,
            CAMPAIGN_STATUS_ENUM.WAIT_FOR_PAYMENT,
            CAMPAIGN_STATUS_ENUM.READY_FOR_LAUNCH,
            CAMPAIGN_STATUS_ENUM.EXPIRED,
            CAMPAIGN_STATUS_ENUM.FULFILLED,
            CAMPAIGN_STATUS_ENUM.PAID_OUT,
        ].includes(campaignStatus)
        ? Boolean(checkPermissions)
        : false
}

/**
 * Hook to check if budget is to be displayed
 *
 * @param isCampaignPrivate
 * @returns true if budget will be shown
 */
export const useShowPrivateCampaignBudget = (isCampaignPrivate: boolean): boolean => {
    const isInternalUser = useSelector(selectIsInternalUser)
    const hasShowPrivateCampaignBudgetPermission = usePermission(CampaignManagement.ShowPrivateCampaignBudget)

    if (isInternalUser || !isCampaignPrivate) return true

    return hasShowPrivateCampaignBudgetPermission
}

/**
 * Hook to check if Payment tab in campaign editor is to be displayed
 *
 * @param campaignHasBudget
 * @param isCampaignPrivate
 * @returns true if payment tab will be shown
 */
export const useShowCampaignPaymentTab = (campaignHasBudget?: boolean, isCampaignPrivate?: boolean): boolean => {
    const isInternalUser = useSelector(selectIsInternalUser)
    const isExternalUser = useSelector(selectIsExternalUser)
    const canViewPrivateCampaignBudget = usePermission(CampaignManagement.ShowPrivateCampaignBudget)
    const canExternalViewPaymentTab = isExternalUser && usePermission(CampaignStatusControl.PayCampaign)
    const canInternalViewPaymentTab = isInternalUser && usePermission(CampaignStatusControl.LaunchCampaign)

    if (!campaignHasBudget) {
        return false
    }

    if (isExternalUser && isCampaignPrivate) {
        return canViewPrivateCampaignBudget && canExternalViewPaymentTab
    }

    return canInternalViewPaymentTab || canExternalViewPaymentTab
}

const templateInternalAllowEditStatus = [
    CAMPAIGN_STATUS_ENUM.DRAFT,
    CAMPAIGN_STATUS_ENUM.FOR_EDIT,
    CAMPAIGN_STATUS_ENUM.FOR_REVIEW,
    CAMPAIGN_STATUS_ENUM.READY_FOR_LAUNCH,
    CAMPAIGN_STATUS_ENUM.WAIT_FOR_PAYMENT,
]
export const useCheckTemplatePermission = (
    campaignStatus?: CAMPAIGN_STATUS_ENUM,
    checkForLiveEditBriefAndDeadline = false,
    launchFieldEditable = false,
    isBudgetField = false,
) => {
    const isInternalUser = useSelector(selectIsInternalUser)

    const isPermissionEditable = useCheckEditTemplatedCampaignPermission(
        campaignStatus || undefined,
        checkForLiveEditBriefAndDeadline,
        launchFieldEditable,
    )

    if (
        isBudgetField &&
        campaignStatus &&
        [
            CAMPAIGN_STATUS_ENUM.READY_FOR_LAUNCH,
            CAMPAIGN_STATUS_ENUM.WAIT_FOR_PAYMENT,
            CAMPAIGN_STATUS_ENUM.LIVE,
            CAMPAIGN_STATUS_ENUM.EXPIRED,
            CAMPAIGN_STATUS_ENUM.FULFILLED,
            CAMPAIGN_STATUS_ENUM.PAID_OUT,
        ].includes(campaignStatus)
    ) {
        return true
    }

    // allow internal user to edit campaign in allowEditStatus
    const isReadOnly =
        campaignStatus && isInternalUser && templateInternalAllowEditStatus.includes(campaignStatus)
            ? false
            : !isPermissionEditable

    return isReadOnly
}
